import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { IconButton } from '~/chords';
import { cn } from '~/lib/cn';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

const MUSIC_IQ_TOOLTIP_STORAGE_KEY = 'hasViewedMusicIqTooltip';
const MUSIC_IQ_ROUTE = '/data/music-iq';

export function MusicIqButton({ hideTooltip }: { hideTooltip: boolean }) {
  const { pathname } = useLocation();
  const { t } = useTranslation('wmgone', {
    keyPrefix: 'globalHeader',
  });
  const [isMusicIqChatbotEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgDataMusicIqChatbotEnabled,
  ]);
  const [showTooltip, setShowTooltip] = useState(false);
  const isMusicIqRoute = useMemo(
    () => pathname.startsWith(MUSIC_IQ_ROUTE),
    [pathname],
  );

  useEffect(() => {
    const hasViewedMusicIqTooltip = localStorage.getItem(
      MUSIC_IQ_TOOLTIP_STORAGE_KEY,
    );

    setShowTooltip(hasViewedMusicIqTooltip !== 'true');
  }, []);

  const onCloseTooltip = () => {
    localStorage.setItem(MUSIC_IQ_TOOLTIP_STORAGE_KEY, 'true');
    setShowTooltip(false);
  };

  if (!isMusicIqChatbotEnabled) {
    return null;
  }

  return (
    <div className="relative flex items-stretch">
      {showTooltip && !isMusicIqRoute && !hideTooltip ? (
        <div className="absolute right-full top-0 z-50 mr-md flex w-[264px] flex-col gap-sm rounded-sm border-px border-solid p-md shadow-xl drop-shadow-xl bg-level-02 border-emphasis-strong">
          <div className="flex items-center justify-between gap-md">
            <span className="text-body-md font-semibold text-default">
              {t('musicIq.tooltip.title')}
            </span>
            <div className="-mr-sm -mt-sm">
              <IconButton
                iconType="xmark"
                onClick={onCloseTooltip}
                aria-label="Close"
              />
            </div>
          </div>
          <span className="text-body-xs leading-4 text-default">
            {t('musicIq.tooltip.description')}
          </span>
        </div>
      ) : null}
      <Link
        to={MUSIC_IQ_ROUTE}
        onClick={onCloseTooltip}
        className={cn(
          'group relative z-0 flex items-stretch rounded-full p-px',
          'bg-[linear-gradient(90deg,oklch(var(--bg-tertiary-default)),oklch(var(--bg-primary-default)))]',
        )}
      >
        <div
          className={cn(
            'relative z-10 flex w-max items-center gap-sm rounded-full px-[14px]',
            'bg-[linear-gradient(180deg,oklch(var(--bg-level-05))50%,oklch(var(--bg-level-04))57%,oklch(var(--bg-level-02))100%)]',
            'bg-[length:auto_200%] bg-[0_100%] duration-300 ease-out hover:bg-[0_0]',
          )}
        >
          <span className="whitespace-nowrap text-body-sm font-bold text-default">
            {t('musicIq.button')}
          </span>
          <img
            src="/images/music-iq/music-iq.svg"
            aria-label="Music IQ Logo"
            alt="Music IQ Logo"
            className="size-[20px]"
          />
        </div>
      </Link>
    </div>
  );
}

MusicIqButton.displayName = 'MusicIqButton';
